/**
 * Map Initial Position Controller
 *
 * Manages the lifecycle of map initialization, distinguishing between
 * the "initial optimal view" phase and subsequent "user interaction" phase.
 *
 * This is used to:
 * 1. Store the optimal view calculated on page load
 * 2. Prevent query param updates during initialization
 * 3. Restore the initial view when navigating back with no query params
 */

import type { MapView } from './mapViewTypes';

/**
 * Singleton controller for managing map initial position state
 */
export class MapInitialPositionController {
  /**
   * The optimal view calculated on initial page load
   */
  private initialOptimalView: MapView | null = null;

  /**
   * Whether we're still in the initialization phase
   * Set to true when initialization completes
   */
  private isInitializationComplete = false;

  /**
   * Whether the user has interacted with the map (panned/zoomed)
   */
  private hasUserInteracted = false;

  /**
   * Set the optimal view calculated during initialization
   */
  setInitialView(view: MapView): void {
    this.initialOptimalView = view;
  }

  /**
   * Get the initial optimal view
   */
  getInitialView(): MapView | null {
    return this.initialOptimalView;
  }

  /**
   * Mark that initialization is complete
   * After this, changes to map bounds are considered user interaction
   */
  markInitializationComplete(): void {
    this.isInitializationComplete = true;
  }

  /**
   * Check if still initializing
   */
  isInInitialPhase(): boolean {
    return !this.isInitializationComplete;
  }

  /**
   * Mark that user has interacted with the map
   * This prevents URL updates based on initial positioning
   */
  markUserInteraction(): void {
    this.hasUserInteracted = true;
  }

  /**
   * Check if user has interacted with the map
   */
  hasUserInteractedWithMap(): boolean {
    return this.hasUserInteracted;
  }

  /**
   * Reset state (useful for navigation between SRPs)
   */
  reset(): void {
    this.isInitializationComplete = false;
    this.hasUserInteracted = false;
    // Keep initialOptimalView for restoration on back navigation
  }

  /**
   * Completely clear state (e.g., when leaving the page)
   */
  clear(): void {
    this.initialOptimalView = null;
    this.isInitializationComplete = false;
    this.hasUserInteracted = false;
  }
}

/**
 * Global singleton instance
 * This persists across component mounts/unmounts within the same page load
 */
let instance: MapInitialPositionController | null = null;

/**
 * Get the global singleton instance
 */
export function getMapInitialPositionController(): MapInitialPositionController {
  if (!instance) {
    instance = new MapInitialPositionController();
  }
  return instance;
}

/**
 * Create a new instance (for testing)
 */
export function createMapInitialPositionController(): MapInitialPositionController {
  return new MapInitialPositionController();
}
